import { originInspectionHeaderShape, originInspectionItemShape } from './origin-inspection-schema';
import { z } from 'zod';
import { insuranceHeaderShape } from './insurance-schema';

import type { Currency } from '../../server/contracts';
import { AppError } from '../../server/errors';
import { pictureDataUriSchema } from './picture-schema';
import { transportHeaderShape, transportItemShape } from './transport-schema';

const currencySchema = z.enum(['USD', 'EUR', 'GBP', 'HKD', 'CNY']) satisfies z.ZodType<Currency>;
const decimalPattern = /^(?:0|[1-9]\d{0,17})(?:\.\d{1,6})?$/;

const nullableDecimalSchema = z.preprocess(
  blankToNull,
  z.union([
    z.string().regex(decimalPattern, 'Must be a nonnegative decimal string with at most 6 decimal places.'),
    z.null(),
  ]),
);

const nullableText = (maxLength: number) => z.preprocess(
  blankToNull,
  z.union([z.string().trim().min(1).max(maxLength), z.null()]),
);

const nullableDate = z.preprocess(
  blankToNull,
  z.union([
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isCalendarDate, 'Must be a valid YYYY-MM-DD date.'),
    z.null(),
  ]),
);

export const supplierSchema = z.object({
  name: nullableText(300).default(null),
  attention: nullableText(200).default(null),
  address: nullableText(1_000).default(null),
  country: nullableText(100).default(null),
}).strict();

export const tradeItemSchema = z.object({
  ...originInspectionItemShape,
  ...transportItemShape,
  id: z.string().uuid(),
  name: nullableText(500),
  description: nullableText(2_000).default(null),
  model: nullableText(200),
  unit: nullableText(30),
  quantity: nullableDecimalSchema,
  unitPrice: nullableDecimalSchema,
  freeSampleReason: nullableText(500),
  remark: nullableText(1_000).default(null),
  marks: nullableText(1_000).optional(),
  packages: nullableText(200).optional(),
  grossWeight: nullableText(200).optional(),
  measurement: nullableText(200).optional(),
  container: nullableText(200).optional(),
  hsCode: nullableText(200).optional(),
  cartonNumber: nullableText(200).optional(),
  netWeight: nullableText(200).optional(),
  pictureDataUri: pictureDataUriSchema.nullable().optional(),
}).strict();

export const tradeInputSchema = z.object({
  ...originInspectionHeaderShape,
  ...insuranceHeaderShape,
  ...transportHeaderShape,
  customerId: z.union([z.string().uuid(), z.null()]),
  buyer: z.object({
    name: nullableText(300),
    attention: nullableText(200).default(null),
    address: nullableText(1_000),
    country: nullableText(100),
  }).strict(),
  supplier: supplierSchema.default({ name: null, attention: null, address: null, country: null }),
  currency: z.union([currencySchema, z.null()]),
  vessel: nullableText(200).optional(),
  voyageNumber: nullableText(100).optional(),
  portOfLoading: nullableText(300).optional(),
  portOfDischarge: nullableText(300).optional(),
  shipper: nullableText(1_000).optional(),
  consignee: nullableText(1_000).optional(),
  notifyParty: nullableText(1_000).optional(),
  freightTerms: nullableText(200).optional(),
  etd: nullableText(100).optional(),
  cutOff: nullableText(100).optional(),
  specialRequirements: nullableText(2_000).optional(),
  carrierSignature: nullableText(200).optional(),
  invoiceNumber: nullableText(200).optional(),
  contractNumber: nullableText(200).optional(),
  lcNumber: nullableText(200).optional(),
  eta: nullableText(100).optional(),
  countryOfOrigin: nullableText(100).optional(),
  destinationCountry: nullableText(100).optional(),
  issuingBankName: nullableText(300).optional(),
  issuingBankAddress: nullableText(1000).optional(),
  billOfLadingNumber: nullableText(200).optional(),
  certificateStatement: nullableText(2000).optional(),
  dispatchedDocuments: nullableText(2000).optional(),
  beneficiaryRepresentative: nullableText(300).optional(),
  invoiceDate: nullableDate.optional(),
  shipmentDate: nullableDate.optional(),
  exchangeRate: nullableDecimalSchema.optional(),
  dispatchDays: z.preprocess(blankToNull, z.string().regex(/^(?:0|[1-9]\d{0,3})$/).nullable()).optional(),
  items: z.array(tradeItemSchema).max(100),
  incoterm: nullableText(30),
  namedPlace: nullableText(300),
  deliveryTerms: nullableText(500).default(null),
  paymentMethod: z.union([z.enum(['TT', 'OTHER']), z.null()]),
  paymentTerms: nullableText(1_000),
  shippingTerms: nullableText(2_000).default(null),
  paymentClause: nullableText(2_000).default(null),
  insuranceClause: nullableText(2_000).default(null),
  documentsRequired: nullableText(2_000).default(null),
  inspectionClause: nullableText(2_000).default(null),
  forceMajeureClause: nullableText(2_000).default(null),
  claimsClause: nullableText(2_000).default(null),
  arbitrationClause: nullableText(2_000).default(null),
  bankAccountVersionId: z.union([z.string().uuid(), z.null()]),
  validityDate: nullableDate,
  deliveryDate: nullableDate,
  shippingMethod: nullableText(200).default(null),
  shippingMarks: nullableText(1_000).default(null),
  freight: nullableDecimalSchema.default('0'),
  insurance: nullableDecimalSchema.default('0'),
  otherCharges: nullableDecimalSchema.default('0'),
  discount: nullableDecimalSchema.default('0'),
  note: z.string().max(4_000),
  authorizedSignature: nullableText(200).optional(),
}).strict().superRefine((input, context) => {
  if (input.items.reduce((size, item) => size + (item.pictureDataUri?.length ?? 0), 0) > 1024 * 1024) {
    context.addIssue({ code: 'custom', path: ['items'], message: 'Product pictures exceed the document size limit.' });
  }
  const ids = new Set<string>();
  input.items.forEach((item, index) => {
    if (ids.has(item.id)) {
      context.addIssue({ code: 'custom', path: ['items', index, 'id'], message: 'Item IDs must be unique.' });
    }
    ids.add(item.id);
  });
});

export type TradeInput = z.output<typeof tradeInputSchema>;
export type TradeItemInput = z.output<typeof tradeItemSchema>;

export function parseTradeInput(value: unknown): TradeInput {
  const parsed = tradeInputSchema.safeParse(value);
  if (!parsed.success) {
    throw new AppError('VALIDATION_FAILED', 422, 'Trade input is invalid.');
  }
  return parsed.data;
}

function blankToNull(value: unknown): unknown {
  return typeof value === 'string' && value.trim() === '' ? null : value;
}

function isCalendarDate(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
